/**
 * QboSection — bloc « QuickBooks en ligne » des réglages d'un dossier Tenue de livres (Entreprise & taxes) :
 * état de la connexion, Connecter (consentement Intuit), Déconnecter, Reprendre les soldes (ouvre QboImportDialog).
 */
import { useCallback, useEffect, useState } from "react";
import { Link2, Link2Off, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, errMsg } from "../../comptabilite/_shared";
import IntuitFormInfo from "./IntuitFormInfo";
import QboSyncPanel from "./QboSyncPanel";

export function useQboConnection(masterId) {
  const [conn, setConn] = useState(null);
  const load = useCallback(async () => {
    if (!masterId) return;
    try { const r = await api.get(`/qbo/connections/${masterId}`); setConn(r.data); }
    catch { setConn({ connected: false, configured: false }); }
  }, [masterId]);
  useEffect(() => { load(); }, [load]);
  return { conn, reload: load };
}

export default function QboSection({ masterId, onOpenImport }) {
  const { conn, reload } = useQboConnection(masterId);
  const [busy, setBusy] = useState(null);

  const connect = async () => {
    setBusy("connect");
    try { const r = await api.post(`/qbo/connections/${masterId}/authorize`); window.location.assign(r.data.url); }
    catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const disconnect = async () => {
    if (!window.confirm(`Déconnecter QuickBooks (${conn.company_name || conn.realm_id}) ? Les comptes et soldes déjà importés restent dans le dossier.`)) return;
    setBusy("disconnect");
    try { await api.delete(`/qbo/connections/${masterId}`); toast.success("QuickBooks déconnecté"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  if (!conn) return <div className="text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> QuickBooks…</div>;
  const li = conn.last_import;
  return (
    <div className={`rounded-xl border-2 p-3 space-y-2 ${conn.connected ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`} data-testid="qbo-section">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-lg bg-[#2CA01C] text-white font-black flex items-center justify-center text-sm shrink-0" title="QuickBooks en ligne">qb</div>
        <div className="flex-1 min-w-[220px] text-xs">
          <div className="font-bold text-slate-800">QuickBooks en ligne {conn.environment === "sandbox" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="qbo-env">bac à sable</span>}</div>
          {conn.connected ? (
            <div className="text-slate-600" data-testid="qbo-status">
              Connecté à <strong data-testid="qbo-company">{conn.company_name || `compagnie ${conn.realm_id}`}</strong>
              {conn.connected_at && <> depuis le {conn.connected_at.slice(0, 10)}</>}
              {conn.needs_reconnect && <span className="ml-1 text-rose-700 font-semibold">— reconnexion requise</span>}
              {li ? <div className="text-[11px] text-slate-500">Dernière reprise : {li.as_of} ({li.accounts} compte(s), {li.created} créé(s)) le {li.at?.slice(0, 10)}</div>
                : <div className="text-[11px] text-slate-500">Aucune reprise des soldes pour l'instant.</div>}
            </div>
          ) : !conn.configured ? (
            <div className="text-rose-700" data-testid="qbo-status">Clés Intuit à configurer sur le serveur (QBO_CLIENT_ID / QBO_CLIENT_SECRET).</div>
          ) : (
            <div className="text-slate-600" data-testid="qbo-status">Non connecté. Le bouton ouvre la page de consentement Intuit ; choisissez la compagnie QuickBooks de ce client.</div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {conn.connected && <Btn tone="sky" icon={RefreshCw} onClick={onOpenImport} testId="qbo-import-btn">Reprendre les soldes…</Btn>}
          {conn.connected
            ? <Btn tone="slate" icon={Link2Off} onClick={disconnect} busy={busy === "disconnect"} testId="qbo-disconnect-btn">Déconnecter</Btn>
            : <Btn icon={Link2} onClick={connect} busy={busy === "connect"} disabled={!conn.configured} testId="qbo-connect-btn">Connecter QuickBooks</Btn>}
        </div>
      </div>
      {!conn.connected && conn.redirect_uri && (
        <p className="text-[10px] text-slate-400" data-testid="qbo-redirect-hint">Adresse de retour à enregistrer dans l'app Intuit (Keys & credentials → Redirect URIs) : <code className="font-mono">{conn.redirect_uri}</code></p>
      )}
      {conn.connected && <QboSyncPanel masterId={masterId} conn={conn} reload={reload} />}
      <IntuitFormInfo />
    </div>
  );
}
